import SalaryCountingDetailsScreen from "../../../screens/SalaryCountingDetails";
import VStack from "../../../ui_atoms/VStack";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import React, { useMemo } from "react";
import { EarningType, ProfileType } from "../../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { buildClientInfo, buildUser } from "../../factories";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";

const DEDUCTIONS = [
    "avs",
    "ac",
    "pc_fam",
    "alloc_fam",
    "laap",
    "laanp",
    "ijm",
    "management_fees",
    "training_costs",
    "lpp",
];
const StoryComponent = ({
    earningType,
    profileType,
    useLseCct = true,
    pay13thSalaryWithSalary = true,
}: {
    earningType: EarningType;
    profileType: ProfileType;
    useLseCct?: boolean;
    pay13thSalaryWithSalary?: boolean;
}) => {
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
            }),
        [],
    );

    const params = {
        earningType: earningType,
        // false → indemnity held as a provision, so the worker part deducts it from the gross
        payVacationIndemnityWithSalary: false,
        // false → co-employment: the client part shows what the client pays this period
        useLseCct,
        pay13thSalaryWithSalary,
        salaryDetails: {
            invoiceSalary: 101500,
            lseMargin: 0,
            grossSalary: 80000,
            netSalary: 75000,
            baseSalary: 85000,
            totalCompensation: 5125,
            thirteenMonthCompensation: 5200,
            publicHolidaysCompensation: 5350,
            vacationCompensation: 6630,
            totalClientDeductions: 2145,
            totalWorkerDeductions: 8960,
            clientDeductions: DEDUCTIONS.map((deduction, idx) => ({
                title: deduction,
                rate: 100,
                amount: (idx + 1) * 550,
            })),

            workerDeductions: DEDUCTIONS.map((deduction, idx) => ({
                title: deduction,
                rate: 100,
                amount: idx * 1025,
            })),
        },
    };
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <VStack alignment="stretch" style={{ flex: 1 }}>
                    {/* @ts-expect-error Stack Component need a complex navigation handler */}
                    <SalaryCountingDetailsScreen route={{ params }} />
                </VStack>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "CreateMission/Salary details",
    component: StoryComponent,
    argTypes: {
        profileType: { type: "symbol" },
        earningType: { type: "symbol" },
        useLseCct: { type: "boolean" },
        pay13thSalaryWithSalary: { type: "boolean" },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const HourlyClient: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
    },
};

export const HourlyWorker: Story = {
    args: {
        profileType: ProfileType.Worker,
        earningType: EarningType.Hourly,
    },
};

export const MonthlyClient: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
    },
};

export const MonthlyWorker: Story = {
    args: {
        profileType: ProfileType.Worker,
        earningType: EarningType.Monthly,
    },
};

export const HourlyNonLseClient: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
        useLseCct: false,
    },
};

export const Monthly13xNonLseClient: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        useLseCct: false,
        pay13thSalaryWithSalary: false,
    },
};
